<template>
  <div class="pdf-placer-wrapper">
    <!-- Toolbar de control -->
    <div class="placer-toolbar">
      <div class="toolbar-left">
        <vs-button
          type="flat"
          icon-pack="feather"
          icon="icon-chevron-left"
          :disabled="currentPage <= 1"
          @click="prevPage"
        />
        <select
          class="page-select"
          :value="currentPage"
          @change="currentPage = Number($event.target.value)"
        >
          <option v-for="p in totalPages" :key="p" :value="p">Pág. {{ p }} / {{ totalPages }}</option>
        </select>
        <vs-button
          type="flat"
          icon-pack="feather"
          icon="icon-chevron-right"
          :disabled="currentPage >= totalPages"
          @click="nextPage"
        />
      </div>
      <div class="toolbar-right">
        <vs-button type="flat" size="small" icon-pack="feather" icon="icon-zoom-out" @click="zoomOut" />
        <span class="zoom-info">{{ Math.round(scale * 100) }}%</span>
        <vs-button type="flat" size="small" icon-pack="feather" icon="icon-zoom-in" @click="zoomIn" />
      </div>
    </div>

    <!-- Contenedor del PDF + Konva overlay -->
    <div class="placer-canvas-container" ref="containerEl">
      <div class="canvas-stack" :style="{ width: pdfWidth + 'px', height: pdfHeight + 'px' }">
        <!-- Canvas del PDF -->
        <canvas ref="pdfCanvas" class="pdf-layer"></canvas>

        <!-- Stage de Konva encima del PDF -->
        <v-stage
          ref="stage"
          :config="stageConfig"
          class="konva-layer"
        >
          <v-layer>
            <!--
              Un v-group por firmante: maneja posición, rotación y drag.
              Todos los hijos usan coordenadas LOCALES (0,0 = esquina sup-izq del recuadro).
              El grupo rota alrededor de su centro gracias a offsetX/offsetY.
            -->
            <v-group
              v-for="(signer, i) in signers"
              v-if="signer.pos_x !== null && signerOnCurrentPage(signer)"
              :key="'group-' + signer.id"
              :config="getGroupConfig(signer)"
              @dragend="onDragEnd($event, i)"
              @dragstart="selectInstance(i)"
              @click="selectInstance(i)"
              @transformend="onTransformEnd($event, i)"
            >
              <!-- Sombra -->
              <v-rect :config="getShadowConfig(signer)" />
              <!-- Rect principal (fondo blanco) -->
              <v-rect :config="getRectConfig(signer, i)" />
              <!-- Avatar: círculo de color -->
              <v-circle :config="getAvatarConfig(signer, i, 'circle')" :listening="false" />
              <!-- Avatar: iniciales -->
              <v-text :config="getAvatarConfig(signer, i, 'text')" :listening="false" />
              <!-- Nombre -->
              <v-text :config="getNameConfig(signer, i)" :listening="false" />
              <!-- Email -->
              <v-text :config="getEmailConfig(signer)" :listening="false" />
              <!-- Botón eliminar (esquina superior derecha) -->
              <v-rect
                :config="getDeleteBtnConfig(signer, 'bg')"
                @click="onDeleteBtnClick($event, i)"
                @tap="onDeleteBtnClick($event, i)"
                @mouseenter="setCursor('pointer')"
                @mouseleave="setCursor('default')"
              />
              <v-text :config="getDeleteBtnConfig(signer, 'icon')" :listening="false" />
              <!-- Botón rotar 90° (a la izquierda del botón eliminar) -->
              <v-rect
                :config="getRotateBtnConfig(signer, 'bg')"
                @click="onRotateBtnClick($event, i)"
                @tap="onRotateBtnClick($event, i)"
                @mouseenter="setCursor('pointer')"
                @mouseleave="setCursor('default')"
              />
              <v-text :config="getRotateBtnConfig(signer, 'icon')" :listening="false" />
              <!-- Botón "Repetir en todas las hojas" (solo si hay más de 1 página) -->
              <template v-if="totalPages > 1">
                <v-rect
                  :config="getRepeatBtnConfig(signer, i, 'bg')"
                  @click="onRepeatBtnClick($event, i)"
                  @tap="onRepeatBtnClick($event, i)"
                  @mouseenter="setCursor('pointer')"
                  @mouseleave="setCursor('default')"
                />
                <v-text :config="getRepeatBtnConfig(signer, i, 'text')" :listening="false" />
              </template>
            </v-group>
          </v-layer>
          <v-layer>
            <v-transformer ref="transformer" :config="transformerConfig" />
          </v-layer>
        </v-stage>
      </div>
    </div>

    <!-- Indicador de firmante activo -->
    <div class="active-signer-info" v-if="activeSigner !== null">
      <feather-icon icon="MapPinIcon" svgClasses="w-4 h-4 text-primary mr-1" />
      <strong>{{ signers[activeSigner].name || ('Firmante ' + (activeSigner + 1)) }}</strong>
      &nbsp;— X: <strong>{{ activeSignerBbox ? activeSignerBbox.x : '—' }}</strong>,
      Y: <strong>{{ activeSignerBbox ? activeSignerBbox.y : '—' }}</strong>,
      Pág: <strong>{{ signers[activeSigner].page === 0 ? 'Última' : signers[activeSigner].page }}</strong>,
      Rot: <strong>{{ signers[activeSigner].rotation || 0 }}°</strong>
      <vs-button
        class="ml-3"
        type="flat"
        size="small"
        color="primary"
        icon-pack="feather"
        icon="icon-bookmark"
        @click="assignCurrentPage"
      >
        Fijar en pág. {{ currentPage }}
      </vs-button>

      <vs-button
        class="ml-2"
        type="flat"
        size="small"
        color="danger"
        icon-pack="feather"
        icon="icon-trash-2"
        @click="removeActiveInstance"
      >
        Quitar de PDF
      </vs-button>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import VueKonva from 'vue-konva';

Vue.use(VueKonva);

// Colores para cada firmante
const SIGNER_COLORS = [
  '#7367F0', // Morado Vuexy Primario
  '#8F5FE8', // Morado Medio
  '#A27BFC', // Morado Claro / Amatista
  '#5A4AD1', // Morado Oscuro
  '#7C3AED', // Violeta
  '#B99CFC', // Lavanda vibrante
  '#4C3CB5', // Índigo/Morado profundo
  '#9E3CF3'  // Orquídea
];

const MAX_REPEAT_PER_PAGE = 3;

export default {
  name: 'PdfSignaturePlacer',
  props: {
    pdfBase64: {
      type: String,
      required: true,
    },
    signers: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      pdfDoc: null,
      currentPage: 1,
      totalPages: 1,
      scale: 1.2,
      pdfWidth: 600,
      pdfHeight: 800,
      activeSigner: null,
      signerColors: SIGNER_COLORS,
    };
  },
  computed: {
    // Coordenadas del bounding box axis-aligned del firmante activo (lo que se ve realmente en pantalla).
    // pos_x/pos_y son la esquina superior-izquierda del rect SIN rotar; para rotaciones de 90°/270°
    // las dimensiones se intercambian y la esquina real cambia respecto al centro.
    activeSignerBbox() {
      if (this.activeSigner === null) return null;
      const s = this.signers[this.activeSigner];
      if (!s || s.pos_x === null || s.pos_x === undefined) return null;
      const w   = s.width  || 140;
      const h   = s.height || 80;
      const rot = s.rotation || 0;
      const cx  = s.pos_x + w / 2;
      const cy  = s.pos_y + h / 2;
      const isRotated = rot === 90 || rot === 270;
      return {
        x: Math.round(cx - (isRotated ? h : w) / 2),
        y: Math.round(cy - (isRotated ? w : h) / 2),
        w: isRotated ? h : w,
        h: isRotated ? w : h,
      };
    },

    stageConfig() {
      return {
        width: this.pdfWidth,
        height: this.pdfHeight,
      };
    },
    transformerConfig() {
      // minSide: lado mínimo permitido en canvas pixels = mínimo alto (52 PDF u.) × scale.
      // Usar el mínimo de los dos ejes para que funcione correctamente con recuadros rotados
      // (90° → ancho y alto se intercambian en stage coords).
      const minSide = 52  * this.scale;
      const maxSide = 200 * this.scale;
      return {
        keepRatio: true,
        rotateEnabled: false,
        enabledAnchors: ['top-left', 'top-right', 'bottom-left', 'bottom-right'],
        borderStroke: '#7367F0',
        borderStrokeWidth: 2,
        borderDash: [4, 2],
        anchorFill: '#ffffff',
        anchorStroke: '#7367F0',
        anchorStrokeWidth: 2,
        anchorSize: 12,
        anchorCornerRadius: 3,
        boundBoxFunc(oldBox, newBox) {
          const newMin = Math.min(Math.abs(newBox.width), Math.abs(newBox.height));
          const newMax = Math.max(Math.abs(newBox.width), Math.abs(newBox.height));
          if (newMin < minSide || newMax > maxSide) return oldBox;
          return newBox;
        },
      };
    },
    groupedSigners() {
      const groups = {};
      const result = [];
      let userCount = 0;

      this.signers.forEach((signer, index) => {
        if (!groups[signer.email]) {
          groups[signer.email] = {
            name: signer.name || 'Sin nombre',
            email: signer.email,
            colorIndex: userCount % this.signerColors.length,
            instances: [],
            placedCount: 0,
            notPlacedCount: 0
          };
          result.push(groups[signer.email]);
          userCount++;
        }
        groups[signer.email].instances.push({
          ...signer,
          originalIndex: index
        });
        if (signer.pos_x !== null) {
          groups[signer.email].placedCount++;
        } else {
          groups[signer.email].notPlacedCount++;
        }
      });
      return result;
    },
    activeSignerEmail() {
      if (this.activeSigner === null || !this.signers[this.activeSigner]) return null;
      return this.signers[this.activeSigner].email;
    }
  },
  watch: {
    pdfBase64(val) {
      if (val) this.loadPdf();
    },
    currentPage() {
      this.renderPage();
      this.detachTransformer();
    },
    scale() {
      this.renderPage();
      // Re-adjuntar el transformer tras el redibujado para que los anchors queden en su sitio
      this.$nextTick(() => {
        if (this.activeSigner !== null) this.attachTransformer(this.activeSigner);
      });
    },
    activeSigner(val) {
      if (val === null) {
        this.detachTransformer();
      } else {
        this.$nextTick(() => this.attachTransformer(val));
      }
    },
  },
  mounted() {
    if (this.pdfBase64) {
      this.loadPdf();
    }
  },
  methods: {
    async loadPdf() {
      try {
        const pdfjsLib = await import('pdfjs-dist/build/pdf');
        pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/2.6.347/pdf.worker.min.js`;

        let data = this.pdfBase64;
        if (data.includes(',')) {
          data = data.split(',')[1];
        }
        const binaryStr = atob(data);
        const bytes = new Uint8Array(binaryStr.length);
        for (let i = 0; i < binaryStr.length; i++) {
          bytes[i] = binaryStr.charCodeAt(i);
        }

        this.pdfDoc = await pdfjsLib.getDocument({ data: bytes }).promise;
        this.totalPages = this.pdfDoc.numPages;
        this.currentPage = 1;
        await this.renderPage();

        this.signers.forEach((signer) => {
          if (!signer.pos_x || signer.pos_x === 100) {
            this.$set(signer, 'pos_x', null);
            this.$set(signer, 'pos_y', null);
            this.$set(signer, 'width', 140);
            this.$set(signer, 'height', 80);
            this.$set(signer, 'page', 1);
            this.$set(signer, 'rotation', 0);
          }
        });
      } catch (e) {
        console.error('Error loading PDF:', e);
      }
    },

    async renderPage() {
      if (!this.pdfDoc) return;
      const page = await this.pdfDoc.getPage(this.currentPage);
      const viewport = page.getViewport({ scale: this.scale });

      const canvas = this.$refs.pdfCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      this.pdfWidth = viewport.width;
      this.pdfHeight = viewport.height;

      await page.render({ canvasContext: ctx, viewport }).promise;
    },

    signerOnCurrentPage(signer) {
      if (signer.page === 0) return true;
      return signer.page === this.currentPage;
    },

    // ─── Configs del grupo (posición y rotación) ──────────────────────────────
    // El grupo se posiciona en su CENTRO (offsetX/offsetY = la mitad del recuadro)
    // para que la rotación gire alrededor del centro visual.
    getGroupConfig(signer) {
      const w    = (signer.width  || 140) * this.scale;
      const h    = (signer.height || 80)  * this.scale;
      const pdfW = this.pdfWidth;
      const pdfH = this.pdfHeight;
      const rot  = signer.rotation || 0;
      const rad  = (rot * Math.PI) / 180;
      // Bounding box del recuadro rotado en coordenadas de stage (canvas pixels).
      // Para 0°/180°: bboxW=w, bboxH=h. Para 90°/270°: bboxW=h, bboxH=w.
      const bboxW = Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad));
      const bboxH = Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad));
      return {
        id: 'group-' + signer.id,
        x: signer.pos_x * this.scale + w / 2,
        y: signer.pos_y * this.scale + h / 2,
        offsetX: w / 2,
        offsetY: h / 2,
        rotation: rot,
        draggable: true,
        // pos.x/pos.y es el CENTRO del grupo. Se clampea con el bbox rotado real
        // para que el recuadro nunca salga del documento independientemente de la rotación.
        dragBoundFunc(pos) {
          return {
            x: Math.max(bboxW / 2, Math.min(pos.x, pdfW - bboxW / 2)),
            y: Math.max(bboxH / 2, Math.min(pos.y, pdfH - bboxH / 2)),
          };
        },
      };
    },

    // ─── Calcula métricas de layout en unidades PDF ───────────────────────────
    _metrics(signer) {
      const w  = signer.width  || 140;
      const h  = signer.height || 80;
      const s  = this.scale;
      const repeatH  = this.totalPages > 1 ? Math.max(10, h * 0.18) : 0;
      const contentH = h - repeatH;
      const pad = Math.max(3, w * 0.045);
      const avatarR  = Math.min(Math.max(7, w * 0.10), Math.max(6, contentH * 0.27));
      const avatarCX = pad + avatarR;
      const avatarCY = contentH * 0.46;
      // Botones proporcionales al alto del área de contenido (canvas px, clampados 12–18px)
      const btnPx  = Math.round(Math.min(Math.max(12, contentH * s * 0.22), 18));
      const btnGap = Math.max(2, Math.round(btnPx * 0.2));
      // Área reservada por los 2 botones + gap + margen interior, en unidades PDF
      const btnsW  = (btnPx * 2 + btnGap + 4) / s;
      const textX  = avatarCX + avatarR + pad * 0.6;
      const textW  = Math.max(12, w - textX - btnsW - pad * 0.3);
      const nameFz   = Math.min(Math.max(5.5, contentH * 0.165), 12);
      const emailFz  = Math.min(Math.max(4.5, contentH * 0.120), 10);
      const nameY    = contentH * 0.15;
      const emailY   = contentH * 0.40;
      return { w, h, s, repeatH, contentH, pad, avatarR, avatarCX, avatarCY, textX, textW, nameFz, emailFz, nameY, emailY, btnPx, btnGap };
    },

    // ─── Configs de elementos hijos (coordenadas LOCALES: 0,0 = sup-izq del grupo) ──

    getShadowConfig(signer) {
      const w = (signer.width  || 140) * this.scale;
      const h = (signer.height || 80)  * this.scale;
      return {
        x: 3, y: 4,
        width: w, height: h,
        fill: 'rgba(0,0,0,0.12)',
        cornerRadius: 10 * this.scale,
        listening: false,
      };
    },

    getRectConfig(signer, i) {
      const colorIndex = this.getSignerColorIndex(signer.email);
      const color = this.signerColors[colorIndex];
      const isActive = this.activeSigner === i;
      const w = (signer.width  || 140) * this.scale;
      const h = (signer.height || 80)  * this.scale;
      return {
        x: 0, y: 0,
        width: w, height: h,
        fill: '#ffffff',
        stroke: isActive ? color : 'rgba(0,0,0,0.10)',
        strokeWidth: (isActive ? 2.5 : 1) * this.scale,
        cornerRadius: 10 * this.scale,
        shadowEnabled: isActive,
        shadowColor: color,
        shadowBlur: 12 * this.scale,
        shadowOpacity: 0.35,
      };
    },

    getAvatarConfig(signer, i, type) {
      const colorIndex = this.getSignerColorIndex(signer.email);
      const color = this.signerColors[colorIndex];
      const m = this._metrics(signer);
      const cx = m.avatarCX * m.s;
      const cy = m.avatarCY * m.s;
      const r  = m.avatarR  * m.s;

      if (type === 'circle') {
        return { x: cx, y: cy, radius: r, fill: color, listening: false };
      }
      const name = signer.name || signer.email || '?';
      const parts = name.trim().split(' ');
      const initials = parts.length >= 2
        ? (parts[0][0] + parts[1][0]).toUpperCase()
        : name.substring(0, 2).toUpperCase();
      return {
        x: cx - r, y: cy - r,
        width: r * 2, height: r * 2,
        text: initials,
        fontSize: Math.max(6, m.avatarR * 0.65) * m.s,
        fontStyle: 'bold',
        fontFamily: 'Inter, sans-serif',
        fill: '#ffffff',
        align: 'center',
        verticalAlign: 'middle',
        listening: false,
      };
    },

    getNameConfig(signer, i) {
      const colorIndex = this.getSignerColorIndex(signer.email);
      const color = this.signerColors[colorIndex];
      const m = this._metrics(signer);
      return {
        x: m.textX * m.s,
        y: m.nameY * m.s,
        text: signer.name || ('Firmante ' + (i + 1)),
        width: m.textW * m.s,
        fontSize: m.nameFz * m.s,
        fontStyle: 'bold',
        fontFamily: 'Inter, sans-serif',
        fill: color,
        ellipsis: true,
        wrap: 'none',
        listening: false,
      };
    },

    getEmailConfig(signer) {
      const m = this._metrics(signer);
      return {
        x: m.textX  * m.s,
        y: m.emailY * m.s,
        text: signer.email || '',
        width: m.textW * m.s,
        fontSize: m.emailFz * m.s,
        fontFamily: 'Inter, sans-serif',
        fill: '#888888',
        ellipsis: true,
        wrap: 'none',
        listening: false,
      };
    },

    getDeleteBtnConfig(signer, type) {
      const m = this._metrics(signer);
      const { btnPx } = m;
      const x = m.w * m.s - btnPx - 2;
      const y = 2;
      const fz = Math.round(btnPx * 0.6);
      if (type === 'bg') {
        return { x, y, width: btnPx, height: btnPx, fill: 'rgba(var(--vs-danger), 1)', cornerRadius: Math.round(btnPx * 0.3) };
      }
      return { x: x + (btnPx - fz) / 2, y: y + (btnPx - fz) / 2 + 1, text: '✕', fontSize: fz, fill: 'white', fontStyle: 'bold', listening: false };
    },

    getRotateBtnConfig(signer, type) {
      const m = this._metrics(signer);
      const { btnPx, btnGap } = m;
      const x = m.w * m.s - btnPx * 2 - btnGap - 2;
      const y = 2;
      const fz = Math.round(btnPx * 0.72);
      if (type === 'bg') {
        return { x, y, width: btnPx, height: btnPx, fill: 'rgba(var(--vs-info), 1)', cornerRadius: Math.round(btnPx * 0.3) };
      }
      return { x: x + (btnPx - fz) / 2, y: y + (btnPx - fz) / 2, text: '↻', fontSize: fz, fill: 'white', fontStyle: 'bold', listening: false };
    },

    getRepeatBtnConfig(signer, i, type) {
      const colorIndex = this.getSignerColorIndex(signer.email);
      const color = this.signerColors[colorIndex];
      const m = this._metrics(signer);
      // Posición local: franja inferior del recuadro
      const x  = 0;
      const y  = (m.h - m.repeatH) * m.s;
      const w  = m.w * m.s;
      const bh = m.repeatH * m.s;
      const fz = Math.min(Math.max(5.5, m.repeatH * 0.50), 8) * m.s;
      if (type === 'bg') {
        return { x, y, width: w, height: bh, fill: color, cornerRadius: [0, 0, 10 * m.s, 10 * m.s] };
      }
      return {
        x: x + 4 * m.s,
        y: y + (bh - fz) / 2,
        text: '↕  Repetir en todas las hojas',
        fontSize: fz,
        fill: 'white',
        fontStyle: 'bold',
        listening: false,
      };
    },

    // ─── Eventos de botones (cancelBubble para evitar que suba al grupo) ──────

    onDeleteBtnClick(e, index) {
      e.cancelBubble = true;
      this.removeSpecificInstance(index);
    },

    onRotateBtnClick(e, index) {
      e.cancelBubble = true;
      this.rotateSigner(index);
    },

    onRepeatBtnClick(e, index) {
      e.cancelBubble = true;
      this.repeatOnAllPages(index);
    },

    // ─── Drag y Transform ──────────────────────────────────────────────────────

    onDragEnd(e, signerIndex) {
      const node = e.target; // el grupo
      const signer = this.signers[signerIndex];
      const w = signer.width  || 140;
      const h = signer.height || 80;

      const rot = signer.rotation || 0;
      const rad = (rot * Math.PI) / 180;
      const bboxW = Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad));
      const bboxH = Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad));

      // node.x() / node.y() es el CENTRO del grupo (porque offsetX/offsetY = w/2, h/2)
      const cxPdf = node.x() / this.scale;
      const cyPdf = node.y() / this.scale;

      const docW  = this.pdfWidth  / this.scale;
      const docH = this.pdfHeight / this.scale;

      const clampCx = Math.max(bboxW / 2, Math.min(cxPdf, docW - bboxW / 2));
      const clampCy = Math.max(bboxH / 2, Math.min(cyPdf, docH - bboxH / 2));

      this.$set(this.signers[signerIndex], 'pos_x', Math.round(clampCx - w / 2));
      this.$set(this.signers[signerIndex], 'pos_y', Math.round(clampCy - h / 2));
    },

    onTransformEnd(e, signerIndex) {
      const node = e.target; // el grupo
      const signer = this.signers[signerIndex];
      const scaleX = node.scaleX(); // keepRatio → scaleX === scaleY

      const newWidth  = Math.max(90, Math.round((signer.width  || 140) * scaleX));
      const newHeight = Math.max(52, Math.round((signer.height || 80)  * scaleX));

      const rot = signer.rotation || 0;
      const rad = (rot * Math.PI) / 180;
      const bboxW = Math.abs(newWidth * Math.cos(rad)) + Math.abs(newHeight * Math.sin(rad));
      const bboxH = Math.abs(newWidth * Math.sin(rad)) + Math.abs(newHeight * Math.cos(rad));

      // El centro puede haberse desplazado
      const cxPdf = node.x() / this.scale;
      const cyPdf = node.y() / this.scale;

      const docW  = this.pdfWidth  / this.scale;
      const docH = this.pdfHeight / this.scale;

      const clampCx = Math.max(bboxW / 2, Math.min(cxPdf, docW - bboxW / 2));
      const clampCy = Math.max(bboxH / 2, Math.min(cyPdf, docH - bboxH / 2));

      // Resetear escala: las dimensiones reales quedan en signer.width/height
      node.scaleX(1);
      node.scaleY(1);

      this.$set(this.signers[signerIndex], 'width',  newWidth);
      this.$set(this.signers[signerIndex], 'height', newHeight);
      this.$set(this.signers[signerIndex], 'pos_x',  Math.round(clampCx - newWidth / 2));
      this.$set(this.signers[signerIndex], 'pos_y',  Math.round(clampCy - newHeight / 2));

      // Re-adjuntar transformer para que los anchors reflejen el nuevo tamaño
      this.$nextTick(() => this.attachTransformer(signerIndex));
    },

    // ─── Transformer ──────────────────────────────────────────────────────────

    attachTransformer(signerIndex) {
      if (!this.$refs.transformer || !this.$refs.stage) return;
      const signer = this.signers[signerIndex];
      if (!signer || signer.pos_x === null) return;
      const transformer = this.$refs.transformer.getNode();
      const stage = this.$refs.stage.getStage();
      // Adjuntar al GRUPO (no al rect) para que la caja transformada respete la rotación
      const node = stage.findOne('#group-' + signer.id);
      if (node) {
        transformer.nodes([node]);
        transformer.getLayer().batchDraw();
      }
    },

    detachTransformer() {
      if (!this.$refs.transformer) return;
      const transformer = this.$refs.transformer.getNode();
      transformer.nodes([]);
      if (transformer.getLayer()) transformer.getLayer().batchDraw();
    },

    // ─── Rotación ──────────────────────────────────────────────────────────────

    rotateSigner(index) {
      const signer = this.signers[index];
      const current = signer.rotation || 0;
      // Secuencia: 0 → 90 → 270 → 0 (se omite 180° para evitar texto volteado)
      const next = current === 0 ? 90 : current === 90 ? 270 : 0;

      const w = signer.width  || 140;
      const h = signer.height || 80;

      // Centro actual del recuadro en unidades PDF (el pivote de rotación no cambia)
      const centerX = (signer.pos_x || 0) + w / 2;
      const centerY = (signer.pos_y || 0) + h / 2;

      // Bounding box del recuadro con la NUEVA rotación (unidades PDF)
      const rad   = (next * Math.PI) / 180;
      const bboxW = Math.abs(w * Math.cos(rad)) + Math.abs(h * Math.sin(rad));
      const bboxH = Math.abs(w * Math.sin(rad)) + Math.abs(h * Math.cos(rad));

      // Documento en unidades PDF
      const docW = this.pdfWidth  / this.scale;
      const docH = this.pdfHeight / this.scale;

      // Clampear el centro para que el bbox rotado quede completamente dentro
      const cx = Math.max(bboxW / 2, Math.min(centerX, docW - bboxW / 2));
      const cy = Math.max(bboxH / 2, Math.min(centerY, docH - bboxH / 2));

      const newPosX = Math.round(cx - w / 2);
      const newPosY = Math.round(cy - h / 2);

      this.$set(signer, 'rotation', next);
      this.$set(signer, 'pos_x', newPosX);
      this.$set(signer, 'pos_y', newPosY);

      // vue-konva actualiza el nodo vía setAttrs en el siguiente tick, pero Konva no
      // redibuja automáticamente el layer, por lo que el grupo puede quedar visualmente
      // desfasado hasta que otra acción fuerza el redibujado (como cambiar de página).
      // Solución: forzar setAttrs + batchDraw directamente sobre el nodo Konva.
      this.$nextTick(() => {
        if (!this.$refs.stage) return;
        const stage = this.$refs.stage.getStage();
        const group = stage.findOne('#group-' + signer.id);
        if (group) {
          const cw = w * this.scale;
          const ch = h * this.scale;
          group.setAttrs({
            x:        newPosX * this.scale + cw / 2,
            y:        newPosY * this.scale + ch / 2,
            rotation: next,
            offsetX:  cw / 2,
            offsetY:  ch / 2,
          });
          group.getLayer().batchDraw();
        }
        if (this.activeSigner === index) this.attachTransformer(index);
      });
    },

    // ─── Selección ────────────────────────────────────────────────────────────

    selectInstance(index) {
      this.activeSigner = index;
    },

    selectUser(email) {
      const firstIndex = this.signers.findIndex(s => s.email === email);
      if (firstIndex >= 0) this.activeSigner = firstIndex;
    },

    // ─── Colocación ───────────────────────────────────────────────────────────

    placeSignerOnCurrentPage(index) {
      const centerX = (this.pdfWidth  / 2) / this.scale - 70;
      const centerY = (this.pdfHeight / 2) / this.scale - 40;

      this.$set(this.signers[index], 'pos_x', Math.round(centerX));
      this.$set(this.signers[index], 'pos_y', Math.round(centerY));
      this.$set(this.signers[index], 'page',  this.currentPage);
      if (this.signers[index].rotation === undefined) {
        this.$set(this.signers[index], 'rotation', 0);
      }
      this.activeSigner = index;
    },

    placeNextInstance(email) {
      const nextIndex = this.signers.findIndex(s => s.email === email && s.pos_x === null);
      if (nextIndex >= 0) {
        // Hay una instancia libre → colocarla directamente
        this.placeSignerOnCurrentPage(nextIndex);
      } else {
        // Todas las instancias ya están colocadas → duplicar y colocar la nueva
        this.duplicateUserSigner(email);
      }
    },

    duplicateUserSigner(email) {
      const index = this.signers.findIndex(s => s.email === email);
      if (index >= 0) {
        this.$emit('duplicate-signer', index);
        // Esperar a que el padre agregue la nueva instancia con pos_x: null
        this.$nextTick(() => {
          const newIndex = this.signers.findIndex(s => s.email === email && s.pos_x === null);
          if (newIndex >= 0) this.placeSignerOnCurrentPage(newIndex);
        });
      }
    },

    // ─── Repetir en todas las hojas ───────────────────────────────────────────

    repeatOnAllPages(signerIndex) {
      const source = this.signers[signerIndex];
      const email  = source.email;

      // Contar instancias existentes por página para este firmante
      const pageCount = {};
      this.signers
        .filter(s => s.email === email && s.pos_x !== null)
        .forEach(s => {
          const p = s.page === 0 ? this.totalPages : s.page;
          pageCount[p] = (pageCount[p] || 0) + 1;
        });

      // Hallar el mínimo de copias entre todas las páginas (sin representar = 0)
      let minCount = Infinity;
      for (let p = 1; p <= this.totalPages; p++) {
        minCount = Math.min(minCount, pageCount[p] || 0);
      }

      if (minCount >= MAX_REPEAT_PER_PAGE) {
        this.$store.dispatch('alert/success', `Esta firma ya tiene ${MAX_REPEAT_PER_PAGE} copias en todas las hojas (límite máximo)`);
        return;
      }

      // Añadir 1 copia solo a las páginas que tienen el mínimo actual
      const pagesToAdd = [];
      for (let p = 1; p <= this.totalPages; p++) {
        if ((pageCount[p] || 0) === minCount) pagesToAdd.push(p);
      }

      this.$emit('repeat-signer-on-pages', {
        sourceIndex: signerIndex,
        pages: pagesToAdd,
        pos_x: source.pos_x,
        pos_y: source.pos_y,
        width: source.width  || 140,
        height: source.height || 80,
        rotation: source.rotation || 0,
      });
    },

    // ─── Eliminar instancias ──────────────────────────────────────────────────

    clearAllInstances(email) {
      // Desactivar selección y limpiar transformer ANTES de cualquier mutación
      this.activeSigner = null;
      this.detachTransformer();

      const indices = this.signers
        .map((s, i) => (s.email === email ? i : -1))
        .filter(i => i !== -1);

      // Eliminar duplicados de atrás hacia adelante para no romper índices
      indices.slice(1).reverse().forEach(i => {
        this.$emit('remove-signer-instance', i);
      });

      // Resetear posición de la primera instancia
      const first = indices[0];
      if (first !== undefined) {
        this.$set(this.signers[first], 'pos_x', null);
        this.$set(this.signers[first], 'pos_y', null);
        this.$set(this.signers[first], 'page',  1);
      }
    },



    removeActiveInstance() {
      this.removeSpecificInstance(this.activeSigner);
    },

    removeSpecificInstance(index) {
      if (index === null || !this.signers[index]) return;

      const email = this.signers[index].email;
      const userInstances = this.signers.filter(s => s.email === email);

      if (userInstances.length > 1) {
        this.$emit('remove-signer-instance', index);
        if (this.activeSigner === index) this.activeSigner = null;
      } else {
        this.$set(this.signers[index], 'pos_x', null);
        this.$set(this.signers[index], 'pos_y', null);
        this.$set(this.signers[index], 'page',  1);
        this.$store.dispatch('alert/success', 'Ubicación de firma reseteada');
        if (this.activeSigner === index) this.activeSigner = null;
      }
    },

    // ─── Página ───────────────────────────────────────────────────────────────

    assignCurrentPage() {
      if (this.activeSigner === null) return;
      this.$set(this.signers[this.activeSigner], 'page', this.currentPage);
    },

    getSignerColorIndex(email) {
      const uniqueEmails = this.groupedSigners.map(g => g.email);
      const idx = uniqueEmails.indexOf(email);
      return idx >= 0 ? idx % this.signerColors.length : 0;
    },

    setCursor(cursor) {
      if (this.$refs.stage) {
        this.$refs.stage.getStage().container().style.cursor = cursor;
      }
    },

    prevPage() {
      if (this.currentPage > 1) this.currentPage--;
    },
    nextPage() {
      if (this.currentPage < this.totalPages) this.currentPage++;
    },
    zoomIn() {
      if (this.scale < 2.5) this.scale = parseFloat((this.scale + 0.2).toFixed(1));
    },
    zoomOut() {
      if (this.scale > 0.5) this.scale = parseFloat((this.scale - 0.2).toFixed(1));
    },
  },
};
</script>

<style scoped>
.pdf-placer-wrapper {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.placer-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 6px 12px;
  backdrop-filter: blur(4px);
}

.page-select {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 2px 6px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 6px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  outline: none;
}

.page-select:focus {
  border-color: rgba(65, 115, 255, 0.6);
}

.zoom-info {
  font-size: 0.85rem;
  font-weight: 600;
  padding: 0 8px;
  min-width: 60px;
  text-align: center;
}

.toolbar-left,
.toolbar-right {
  display: flex;
  align-items: center;
}

/* Canvas stack */
.placer-canvas-container {
  overflow: auto;
  border-radius: 8px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: #f5f5f5;
  max-height: 80vh;
  display: flex;
  justify-content: center;
  padding: 20px;
}

.canvas-stack {
  position: relative;
  flex-shrink: 0;
}

.pdf-layer {
  display: block;
}

.konva-layer {
  position: absolute !important;
  top: 0;
  left: 0;
  cursor: default;
}

/* Info del firmante activo */
.active-signer-info {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
  padding: 8px 14px;
  border-radius: 8px;
  background: rgba(var(--vs-primary), 0.08);
  border: 1px solid rgba(var(--vs-primary), 0.2);
  font-size: 0.85rem;
}
</style>
